import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../../api/client'
import BrandWordmark from '../BrandWordmark'

type Props = {
  onLogin: (token: string, user: unknown) => void
  accessDenied?: boolean
  onGoToSite?: () => void
  onLogout?: () => void
}

export default function AdminLoginPage({ onLogin, accessDenied, onGoToSite, onLogout }: Props) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const loginRes = await authAPI.login(email.trim().toLowerCase(), password)
      const newToken = loginRes.data.access_token as string
      const meRes = await authAPI.getCurrentUser(newToken)
      const user = meRes.data
      if (user?.role !== 'admin') {
        setError(t('admin.accessDenied'))
        return
      }
      onLogin(newToken, user)
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        t('admin.loginFailed')
      setError(typeof detail === 'string' ? detail : t('admin.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="vc-admin-login">
      <div className="vc-admin-login__hero">
        <BrandWordmark className="vc-admin-login__logo" title="reDevicer" />
        <p className="vc-admin-login__subtitle">{t('admin.panelSubtitle')}</p>
      </div>

      <form className="vc-admin-login__card" onSubmit={handleSubmit}>
        {accessDenied ? (
          <p className="vc-admin-login__denied" role="alert">
            {t('admin.accessDenied')}
          </p>
        ) : null}
        {error ? (
          <p className="vc-admin-login__error" role="alert">
            {error}
          </p>
        ) : null}

        <label className="vc-admin-login__field">
          <span className="vc-admin-login__label">{t('admin.loginEmail')}</span>
          <input
            className="vc-admin-login__input"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </label>

        <label className="vc-admin-login__field">
          <span className="vc-admin-login__label">{t('admin.loginPassword')}</span>
          <input
            className="vc-admin-login__input"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
        </label>

        <button type="submit" className="vc-admin-login__submit" disabled={loading}>
          {loading ? t('common.loading') : t('admin.login')}
        </button>

        {accessDenied && onGoToSite ? (
          <div className="vc-admin-login__footer">
            <button type="button" className="vc-admin-login__link" onClick={onGoToSite}>
              {t('admin.backToSite')}
            </button>
            {onLogout ? (
              <button type="button" className="vc-admin-login__link" onClick={onLogout}>
                {t('admin.logout')}
              </button>
            ) : null}
          </div>
        ) : null}
      </form>
    </div>
  )
}
